<template>
  <span :class="baseClasses.join(' ')">
    <span :class="valueClasses">{{ watts }}</span>
    <span :class="unitClasses">{{ unit }}</span>
  </span>
</template>

<script>
  import { useStrings } from '@/composables/useStrings.ts'

  export default {
    name: 'wattage',
    setup(){
      const { t } = useStrings()
      return {
        t,
      }
    },
    props: {
      value: {
        type: Number,
        required: true,
      },
      showSign: {
        type: Boolean,
        default: false,
      },
      kwThreshold: {
        type: Number,
        default: 990,
      },
      type: {
        type: String,
      },
      extraClasses: {
        type: Array,
        default: () => {
          return []
        },
      },
    },
    computed: {
      baseClasses: function () {
        let classes = ['wattage']
        if (this.type) {
          classes.push(`wattage--${this.type}`)
        }
        if (this.extraClasses?.length > 0) {
          classes = classes.concat(this.extraClasses)
        }

        if (Math.abs(this.value) >= 10000) {
          classes.push('wattage--large-value')
        }
        return classes
      },
      valueClasses: function () {
        return this.baseClasses.map((cls) => {
          return cls + '__value'
        })
      },
      unitClasses: function () {
        return this.baseClasses.map((cls) => {
          return cls + '__unit'
        })
      },
      watts: function () {
        // We only need to add a plus, since negative numbers automaticalliy get a
        // minus sign.
        const sign = this.showSign && this.value > 0 ? '+' : ''
        let value = this.value

        // We round to 1k above/below 990W.
        if (value > 990 || value < -990) {
          value = (value / 1000).toFixed(1).replace('.', ',')
          return sign + value
        }

        return sign + Math.round(value)
      },
      unit: function () {
        if (this.value > this.kwThreshold || this.value < -this.kwThreshold) {
          return this.t('units.kilowatt')
        }
        return this.t('units.watt')
      },
    },
  }
</script>

<style lang="scss">
  .wattage {
    font-weight: $font-weight-bold;
    line-height: $line-height-min;
    font-size: $wattage-font-size-min;
    transition: $transition-base;
    $large-value-factor: 0.85;

    @media (min-height: 600px) {
      font-size: $wattage-font-size-sm;

      &.wattage--large-value {
        font-size: $wattage-font-size-sm * 0.85;
      }
    }

    @media (min-height: 700px) and (min-width: 500px) {
      font-size: $wattage-font-size-md;

      &.wattage--large-value {
        font-size: $wattage-font-size-md * 0.85;
      }
    }

    @media (min-height: 800px) and (min-width: $bp-sm) {
      font-size: $wattage-font-size-md;

      &.wattage--large-value {
        font-size: $wattage-font-size-md * 0.85;
      }
    }

    @media (min-height: 1000px) and (min-width: $bp-lg) {
      font-size: $wattage-font-size-lg;

      &.wattage--large-value {
        font-size: $wattage-font-size-lg * 0.85;
      }
    }

    &__unit {
      font-size: 70%;
      margin-left: $spacer01;

      &.wattage--large-value {
        font-size: 60%;
      }
    }

    &--positive {
      color: var(--color-positive-value);
    }

    &--negative {
      color: var(--color-negative-value);
    }
  }
</style>
